import React, { useEffect, useState, useContext, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { AuthContext } from "../../../context/AuthContext";
import "./ProductDetail.css";
import { BACKEND_URL } from "../../../config";

const CURRENCY = "€";

function QtySelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 70 });
  const ref = useRef(null);
  const options = [1, 2, 3, 4, 5];

  const toggleOpen = () => {
    if (!open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const menuHeight = 220;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < menuHeight && rect.top > menuHeight;

      setMenuPos({
        top: openUpward ? rect.top - menuHeight - 6 : rect.bottom + 6,
        left: rect.left + rect.width / 2,
        width: rect.width,
      });
    }
    setOpen(!open);
  };

  useEffect(() => {
    const handleOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const closeOnScroll = () => setOpen(false);
    document.addEventListener("mousedown", handleOutside);
    window.addEventListener("scroll", closeOnScroll, true);
    window.addEventListener("resize", closeOnScroll);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("scroll", closeOnScroll, true);
      window.removeEventListener("resize", closeOnScroll);
    };
  }, []);

  return (
    <div className="qty-select" ref={ref} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className={`qty-select-toggle ${open ? "open" : ""}`}
        onClick={toggleOpen}
      >
        <span>{value}</span>
        <svg className="qty-select-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none">
          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <ul
          className="qty-select-menu"
          style={{
            position: "fixed",
            top: menuPos.top,
            left: menuPos.left,
            transform: "translateX(-50%)",
            width: menuPos.width,
          }}
        >
          {options.map((n) => (
            <li
              key={n}
              className={`qty-select-option ${value === n ? "selected" : ""}`}
              onClick={() => { onChange(n); setOpen(false); }}
            >
              {n}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function ProductDetail() {

  const { slug } = useParams();
  const relatedRef = useRef(null);

  const scrollByAmount = (ref, amount) => {
    if (ref.current) {
      ref.current.scrollBy({
        left: amount,
        behavior: "smooth",
      });
    }
  };
  const [openFaq, setOpenFaq] = useState(0);
  const navigate = useNavigate();
  const reviewRef = useRef(null);
  const [product, setProduct] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [qtyData, setQtyData] = useState({});
  const [activePack, setActivePack] = useState(null);
  const [activeTab, setActiveTab] = useState("description");
  const [reviews, setReviews] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [dataLoaded, setDataLoaded] = useState(false);

  const reviewsPerPage = 10;
  const indexOfLast = currentPage * reviewsPerPage;
  const indexOfFirst = indexOfLast - reviewsPerPage;
  const currentReviews = reviews.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(reviews.length / reviewsPerPage);

  const [formData, setFormData] = useState({
    name: "",
    title: "",
    message: "",
    rating: 0
  });

  const { isLoggedIn, setShowAuth } = useContext(AuthContext);

  const loadProduct = useCallback(() => {
    setDataLoaded(false);
    fetch(`${BACKEND_URL}/api.php?module=product`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setAllProducts(data.data);
          let found = null;
          if (slug) {
            found = data.data.find((p) => p.slug === slug);
          }
          setProduct(found);
        }
      })
      .finally(() => setDataLoaded(true));
  }, [slug]);

  const loadReviews = useCallback(() => {
    if (!product || !product.id) return;
    fetch(
      `${BACKEND_URL}/api.php?module=reviews&product_id=${product.id}`,
      { credentials: "include" }
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setReviews(data.data);
        }
      })
      .catch((err) => console.log(err));
  }, [product]);

  useEffect(() => {
    loadProduct();
  }, [slug, loadProduct]);

  useEffect(() => {
    if (product) {
      loadReviews();
    }
  }, [product, loadReviews]);

  useEffect(() => {
    if (activeTab === "reviews" && reviewRef.current) {
      const y =
        reviewRef.current.getBoundingClientRect().top +
        window.pageYOffset - 120;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  }, [currentPage, activeTab]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!isLoggedIn) {
      setShowAuth(true);
      return;
    }

    if (!formData.message || formData.rating === 0) {
      alert("Please fill all fields");
      return;
    }

    const fd = new FormData();
    fd.append("action", "add");
    fd.append("product_id", product.id);
    fd.append("name", formData.name);
    fd.append("rating", formData.rating);
    fd.append("title", formData.title);
    fd.append("comment", formData.message);

    fetch(`${BACKEND_URL}/api.php?module=reviews`, {
      method: "POST",
      credentials: "include",
      body: fd
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          alert("Review submitted successfully. Waiting for admin approval.");
          loadReviews();
          setFormData({ name: "", title: "", message: "", rating: 0 });
        } else {
          alert(data.message || "Failed to submit review");
        }
      })
      .catch((err) => console.log(err));
  };

  const addToCart = (packItem, index) => {
    if (!isLoggedIn) {
      setShowAuth(true);
      return;
    }

    const cart = JSON.parse(localStorage.getItem("cart")) || [];

   const item = {
      id: product.id,
      productName: product.productName,
      categoryName: product.categoryName,
      image: product.image?.startsWith("http")
        ? product.image
        : `${BACKEND_URL}/${product.image}`,
      packSize: `${packItem.packSize} ${packItem.unit}`,
      price: Number(packItem.price),
      qty: Number(qtyData[index] || 1)
    };

    // FIX: matching against existing cart entries was failing whenever
    // the packSize string differed by case or stray whitespace (e.g.
    // trailing/double spaces coming from packItem.unit), so re-adding
    // the SAME pack would silently fall through to "push a new item"
    // instead of incrementing qty. Normalizing both sides (trim +
    // collapse internal whitespace + lowercase) before comparing makes
    // the match reliable regardless of formatting quirks.
    const normalize = (s) =>
      String(s || "").trim().replace(/\s+/g, " ").toLowerCase();

    const existingIndex = cart.findIndex(
      (c) => c.id === item.id && normalize(c.packSize) === normalize(item.packSize)
    );

    if (existingIndex > -1) {
      cart[existingIndex].qty += item.qty;
    } else {
      cart.push(item);
    }

    localStorage.setItem("cart", JSON.stringify(cart));
    window.dispatchEvent(new Event("cartUpdated"));
    alert("Product added to cart");
  };

  const safeJSON = (data) => {
    try {
      return typeof data === "string" ? JSON.parse(data) : data || [];
    } catch {
      return [];
    }
  };

  // ✅ Data aane tak null return — ClientLayout spinner dikhega
  if (!dataLoaded) return null;

  // ✅ Data aa gaya but product nahi mila
  if (!product) {
    return <h2 style={{ textAlign: "center", padding: "40px" }}>Product Not Found</h2>;
  }

  const pack = safeJSON(product.packPricing);
  const faq = safeJSON(product.faq);
  const selectedPack = activePack || pack[0];

  const relatedProducts = allProducts.filter(
    (p) => p.categoryName === product.categoryName && p.id !== product.id
  );

  return (
    <>
      <Helmet>
        <title>
          {product?.productName
            ? `${product.productName} | Kamagra Star`
            : "Product | Kamagra Star"}
        </title>
        <meta
          name="description"
          content={
            product?.description
              ? product.description.replace(/<[^>]+>/g, "").substring(0, 160)
              : "Buy high quality medicines online at Kamagra Star with fast delivery."
          }
        />
        <meta
          name="keywords"
          content={`kamagra star, ${product?.productName || "medicine"}, online pharmacy, healthcare`}
        />
        <meta property="og:title" content={product?.productName || "Kamagra Star Product"} />
        <meta property="og:description" content="Buy medicines online with fast delivery" />
        <meta property="og:type" content="product" />
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      <div className="product-page">

        <div className="product-left">
          <img
            src={
              product.image?.startsWith("http")
                ? product.image
                : `${BACKEND_URL}/${product.image}`
            }
            alt={product.productName}
          />
        </div>

        <div className="right">

          <h1>{product.productName}</h1>

          <div className="product-rating-wrap">
            <div className="product-stars">
              <div className="star-bg">★★★★★</div>
              <div
                className="star-fill"
                style={{
                  width: `${((Number(product?.avg_rating) || 0) / 5) * 100}%`
                }}
              >
                ★★★★★
              </div>
            </div>

            <div className="review-links">
              <span className="review-count-text">
                ({reviews.length}) Reviews
              </span>
              <span className="divider">|</span>
              <span
                className="add-review-link"
                onClick={() => {
                  setActiveTab("reviews");
                  setTimeout(() => {
                    document
                      .querySelector(".review-form")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }, 100);
                }}
              >
                Add Your Review
              </span>
            </div>
          </div>

          <div className="pack-card">
            <table className="pack-table">
              <thead>
                <tr>
                  <th>Pack Size</th>
                  <th>Qty</th>
                  <th>Price</th>
                  <th></th>
                  <th>Unit Price</th>
                </tr>
              </thead>
              <tbody>
                {pack.map((p, i) => (
                  <tr
                    key={i}
                    className={selectedPack === p ? "active-row" : ""}
                    onClick={() => setActivePack(p)}
                  >
                    <td>{p.packSize} {p.unit}</td>
                    <td>
                      <QtySelect
                        value={Number(qtyData[i]) || 1}
                        onChange={(n) => setQtyData({ ...qtyData, [i]: n })}
                      />
                    </td>
                    <td>{CURRENCY}{p.price}</td>
                    <td>
                      <button
                        className="cart-btn"
                        onClick={() => addToCart(p, i)}
                      >
                        Add to Cart
                      </button>
                    </td>
                    <td>{CURRENCY}{p.unitPerPrice}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="save-box">Save more with our biggest pack</div>

          <div className="stock-row">
            <span className="in-stock"> In Stock</span>
            <span><b>SKU:</b> {product.sku}</span>
          </div>

        </div>
      </div>

      {/* TABS - ab .product-page se bahar, full-width section */}

      <div className="tabs-full-width">
        <div className="tabs-right">
          <div className="tab-header" ref={reviewRef}>
            <button
              className={activeTab === "description" ? "active" : ""}
              onClick={() => setActiveTab("description")}
            >
              Description
            </button>
            <button
              className={activeTab === "reviews" ? "active" : ""}
              onClick={() => setActiveTab("reviews")}
            >
              Reviews ({reviews.length})
            </button>
            <button
              className={activeTab === "faq" ? "active" : ""}
              onClick={() => setActiveTab("faq")}
            >
              FAQ
            </button>
            <button
              className={activeTab === "other" ? "active" : ""}
              onClick={() => setActiveTab("other")}
            >
              Other Information
            </button>
          </div>

          <div className="tab-content">

            {activeTab === "description" && (
              <div
                className="description-content"
                dangerouslySetInnerHTML={{ __html: product.description }}
              />
            )}

            {activeTab === "reviews" && (
              <div className="review-section">
                {currentReviews.map((r) => (
                  <div key={r.id} className="review-card">
                    <div className="review-top">
                      <div>
                        <strong className="review-user">{r.name}</strong>
                        <p className="review-date">
                          {new Date(r.createdAt).toDateString()}
                        </p>
                      </div>
                      <span className="stars">
                        {"★".repeat(r.rating)}
                        {"☆".repeat(5 - r.rating)}
                      </span>
                    </div>
                    {r.title && (
                      <h4 className="review-title">{r.title}</h4>
                    )}
                    <p>{r.comment}</p>
                  </div>
                ))}

                {totalPages > 1 && (
                  <div className="pagination">
                    <button
                      disabled={currentPage === 1}
                      onClick={() => {
                        if (currentPage > 1)
                          setCurrentPage((prev) => prev - 1);
                      }}
                    >
                      Prev
                    </button>
                    {[...Array(totalPages)].map((_, i) => (
                      <button
                        key={i}
                        className={currentPage === i + 1 ? "active-page" : ""}
                        onClick={() => setCurrentPage(i + 1)}
                      >
                        {i + 1}
                      </button>
                    ))}
                    <button
                      disabled={currentPage === totalPages}
                      onClick={() => {
                        if (currentPage < totalPages)
                          setCurrentPage((prev) => prev + 1);
                      }}
                    >
                      Next
                    </button>
                  </div>
                )}

                <form className="review-form" onSubmit={handleSubmit}>
                  <h3>Write a review</h3>
                  <div className="rating-input">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span
                        key={star}
                        onClick={() =>
                          setFormData({ ...formData, rating: star })
                        }
                        className={formData.rating >= star ? "star active" : "star"}
                      >
                        ★
                      </span>
                    ))}
                  </div>
                  <div className="row">
                    <input
                      type="text"
                      name="name"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={handleChange}
                    />
                    <input
                      type="text"
                      name="title"
                      placeholder="Review Title"
                      value={formData.title}
                      onChange={handleChange}
                    />
                  </div>
                  <textarea
                    name="message"
                    placeholder="Write your review..."
                    value={formData.message}
                    onChange={handleChange}
                  />
                  <button type="submit">Submit Review</button>
                </form>
              </div>
            )}

            {activeTab === "faq" && (
              <div className="faq-container">
                {faq.map((f, i) => (
                  <div key={i} className="faq-item">
                    <div
                      className="faq-summary"
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    >
                      <span>{f.question}</span>
                      <span
                        className="f-icon"
                        style={{
                          display: "inline-block",
                          transform: openFaq === i ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "0.3s"
                        }}
                      >
                        ⌄
                      </span>
                    </div>
                    {openFaq === i && (
                      <div className="faq-content">{f.answer}</div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {activeTab === "other" && (
              <div>
                <p><b>Manufacturer:</b> {product.manufacturer}</p>
                <p><b>Composition:</b> {product.composition}</p>
                <p><b>Ship Time:</b> {product.shipTime}</p>
                <p><b>Status:</b> {product.status}</p>
              </div>
            )}

          </div>
        </div>
      </div>

      <div className="related-section">
        <h2>Related Products</h2>

        <div className="slider-wrapper">

          <button
            className="scroll-arrow scroll-arrow-left"
            onClick={() => scrollByAmount(relatedRef, -260)}
            type="button"
            aria-label="Scroll left"
          >
            ‹
          </button>

          <div
            className="product-list horizontal"
            ref={relatedRef}
          >
            {relatedProducts.map((item) => {
              const price = safeJSON(item.packPricing)?.[0]?.price || "N/A";

              return (
                <div className="product-card" key={item.id}>
                  <img
                    src={
                      item.image?.startsWith("http")
                        ? item.image
                        : `${BACKEND_URL}/${item.image}`
                    }
                    alt={item.productName}
                    onClick={() => navigate(`/product/${item.slug}`)}
                  />

                  <h3 onClick={() => navigate(`/product/${item.slug}`)}>
                    {item.productName}
                  </h3>

                  <div className="product-rating">
                    <div className="product-stars">
                      <div className="product-star-bg">★★★★★</div>
                      <div
                        className="product-star-fill"
                        style={{
                          width: `${((Number(item.avg_rating) || 0) / 5) * 100}%`,
                        }}
                      >
                        ★★★★★
                      </div>
                    </div>

                    <span className="product-review-count">
                      ({item.review_count || 0})
                    </span>
                  </div>

                  <p className="product-price">
                    Starting at just <span>€{price}</span>
                  </p>

                  <button
                    className="btn"
                    onClick={() => navigate(`/product/${item.slug}`)}
                  >
                    View Product
                  </button>
                </div>
              );
            })}
          </div>

          <button
            className="scroll-arrow scroll-arrow-right"
            onClick={() => scrollByAmount(relatedRef, 260)}
            type="button"
            aria-label="Scroll right"
          >
            ›
          </button>

        </div>
      </div>
    </>
  );
}
